import { Box, Button, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import CustomTable from '../../common/custom-table/CustomTable'
import noAssignmentImg from '../../../assets/noassignment.svg'
import AssignmentViewer from '../../common/AssignmentViewer'
import { ASSIGNMENT_CONVERT_DUE_TIME_BEFORE, formatDate, Icons } from '../../../utils/utilities'
import { useUserStore } from '../../../store/user.store'
import SubmissionsViewer from './SubmissionsViewer'
import api from '../../../service/api'
import dayjs from 'dayjs'


const AssignmentList = () => {

    const [searchParams, setSearchParams] = useSearchParams( { searchText: "", month: "", selectedDepartment: "all", selectedInstitute: "all", view: "tabular", sortByName: "1", sortBySubmissionsCount: "1", sortByAssignmentCount: "1" } )
    const searchText = searchParams.get( 'searchText' ) || ""
    const selectedInstitute = searchParams.get( 'selectedInstitute' ) || "all"
    const selectedDepartment = searchParams.get( 'selectedDepartment' ) || "all"

    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [assignments, setAssignments] = useState( [] )
    const [selectedAssignment, setSelectedAssignment] = useState( null )
    const [submissionViewerState, setSubmissionViewerState] = useState( false )

    const UserStore = useUserStore()

    const { layout } = useTheme()

    const navigate = useNavigate()

    const columns = useMemo( () => [
        { id: 2, field: "assignment_name", name: "Assignment name", width: "200px", padding: "10px 20px" },
        { id: 3, field: "faculty_name", linkGetter: row => `/${window.location.pathname?.includes( 'admin' ) ? "admin" : "audit"}/faculty/${row?.assignment_created_by}?legacy=${row.legacy_system_data}`, renderCell: row => row.faculty_name?.toLowerCase(), cellStyles: { textTransform: "capitalize" }, name: 'Faculty name', width: "150px", padding: "10px 20px" },
        { id: 6, field: "institute", cellStyles: { textTransform: "capitalize" }, name: 'Institute', width: "100px", padding: "10px 20px" },
        { id: 6, field: "department", renderCell: row => row.department?.toLowerCase(), cellStyles: { textTransform: "capitalize" }, name: 'Department', width: "250px", padding: "10px 20px" },
        { id: 4, field: "subject_name", renderCell: row => row.assignment_subject?.subject_name || "Not available", name: "Subject", width: "250px", padding: "10px 20px" },
        { id: 5, field: "assignment_created_at", sortable: true, renderCell: row => dayjs( row.assignment_created_at ).format( "LLL" ), align: "center", name: "Created on", width: "200px", padding: "10px 20px" },
        { id: 7, field: "submissions", sortable: true, align: "center", name: "Submissions", width: "150px", padding: "10px 20px" },
        {
            id: 7,
            field: "actions",
            renderCell: row => (
                <Box display="flex" justifyContent="flex-end" gap="10px" alignItems="center">
                    <IconButton
                        onClick={() => setSelectedAssignment( row )}
                        color="primary"
                        size="small"
                    >
                        {Icons.default.OpenInNewIcon}
                    </IconButton>
                    {+row.submissions > 0 && <Tooltip title="View submissions">
                        <IconButton
                            onClick={() => {
                                setSelectedAssignment( row )
                                setSubmissionViewerState( true )
                            }}
                            color="secondary"
                            size="small"
                        >
                            {Icons.default.AnalyticsIcon}
                        </IconButton>
                    </Tooltip>}
                </Box>
            ), align: "right", name: "Actions", minWidth: "150px", padding: "10px 20px"
        },
    ], [] )


    const filterFunction = item => {
        const searchValue = new RegExp( searchText?.replace( /[.*+?^${}()|[\]\\]/g, '\\$&' ), 'i' )
        if ( selectedDepartment !== "all" ) {
            const [department_id, legacy_department_id] = selectedDepartment?.split( "-" )
            const doesMatchDepartment = ( item.legacy_system_data && +legacy_department_id === +item.department_id ) || ( !item.legacy_system_data && +department_id === +item.department_id )
            return doesMatchDepartment && ( item.department?.match( searchValue ) || item.faculty_name?.match( searchValue ) || item.assignment_created_by?.match( searchValue ) )
        } else {
            return item.department?.match( searchValue ) || item.faculty_name?.match( searchValue ) || item.assignment_created_by?.match( searchValue )
        }
    }

    const removeDeletedAssignment = ( value ) => {
        const newAssignmentList = [...assignments]
        setAssignments( newAssignmentList.filter( a => a.assignment_id !== value.assignment_id ) )
    }

    const removeAppliedFilters = () => {
        setSearchParams( prev => {
            prev.set( 'searchText', '' )
            prev.set( 'selectedInstitute', "all" )
            prev.set( 'selectedDepartment', "all" )
            return prev
        } )
    }

    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data } } = await api.audit.getAssignmentsForAuditing( { institute: selectedInstitute !== "all" ? selectedInstitute : "" } )
            setAssignments( data )
            setError( null )
        } catch ( err ) {
            setError( { code: err?.response?.status, message: err?.message?.data?.message || "Something went wrong while fetching assignments." } )
        } finally {
            setLoading( false )
        }
    }, [selectedInstitute] )


    useEffect( () => {
        getData()
    }, [getData] )

    useEffect( () => {
        console.log( selectedAssignment )
    }, [selectedAssignment] )

    return (
        <Box sx={{ gap: layout.cardsGap, flexShrink: 0, display: "flex", flexDirection: "column" }}>

            <AssignmentViewer onAfterDelete={removeDeletedAssignment} onClose={() => setSelectedAssignment( null )} loaded={true} selectedValue={selectedAssignment} open={!submissionViewerState && Boolean( selectedAssignment )} />
            {selectedAssignment && submissionViewerState && <SubmissionsViewer selectedValue={selectedAssignment} handleClose={() => { setSubmissionViewerState( false ); setSelectedAssignment( null ) }} assignmentId={selectedAssignment?.assignment_id} open={submissionViewerState} />}

            <Box display="flex" gap={layout.cardsGap} alignItems="flex-end" justifyContent="space-between" >
                <Typography variant="h1" fontWeight="600" component="h2" fontSize="14px">Assignments</Typography>
                <Button size="small" onClick={() => navigate( `/${["ADMIN", "SUBADMIN"].includes( UserStore.getUser.user_role?.toUpperCase() ) ? "admin" : "audit"}/assignments` )} variant="contained" color='secondary' disableElevation sx={{ height: "33.5px", textTransform: "capitalize", }} >View Stats</Button>
            </Box >

            {/* Data */}
            {!loading && !error && assignments.length > 0 && <CustomTable containerStyles={{ maxHeight: "450px" }} globalSearch="Search by assignment, faculty, faculty ID or subject..." data={assignments} columns={columns} />}

            {/* No Data */}
            {!loading && !error && assignments.filter( filterFunction ).length === 0 && <Box flexDirection="column" padding="20px" display="flex" alignItems="center" justifyContent="center" gap="20px">
                <img src={noAssignmentImg} height="120px" alt="No assignmets to show" />
                <Typography textAlign="center" variant='subtitle2'>No assignments were found for applied filters!</Typography>
                <Button onClick={removeAppliedFilters} sx={{ textTransform: "capitalize" }} color="secondary" variant="contained" disableElevation>Remove filters</Button>
            </Box>}

            {/* Loading */}
            {loading && !error && <DataLoadingSpinner waitingMessage="Loading assignments please wait...." />}

            {/* Retry */}
            {error && <TryAgainMessageBlock err={error.message} code={error.code} getData={getData} loading={loading} />}
        </Box>
    )
}

export default AssignmentList
